import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface WeatherAlert {
  id: string;
  shop_id: string;
  ticket_id: string;
  bay_id: string;
  scheduled_start: string;
  scheduled_end: string;
  created_at: string;
  updated_at: string;
  status: 'open' | 'dismissed' | 'resolved' | 'auto_resolved';
  reason: 'rain' | 'snow' | 'cold' | 'storm' | 'heat' | 'wind';
  previous_condition: string | null;
  new_condition: string | null;
  temperature_f: number | null;
  notes: string | null;
  /**
   * CORE-BR provenance. NULL on rows written before the provenance columns
   * existed — those rows genuinely do not record which kind of reading they
   * came from, and the card says so rather than assuming.
   */
  observation_kind: 'current_observation' | 'forecast' | null;
  weather_effective_at: string | null;
  weather_source: string | null;
  policy_threshold: string | null;
  /**
   * The explanation as it stood WHEN THE ALERT WAS RAISED. Displayed as
   * stored and never re-derived from today's shop policy — the shop may have
   * changed its threshold since, and re-deriving would rewrite history to
   * match the present.
   */
  policy_explanation: string | null;
  // Joined data
  ticket?: any;
  bay?: any;
}

export function useWeatherAlerts() {
  const { activeShop } = useAuth();
  const [alerts, setAlerts] = useState<WeatherAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAlerts = async () => {
    if (!activeShop?.id) {
      setAlerts([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('weather_alerts')
        .select(`
          *,
          ticket:tickets(
            id,
            customer_name,
            predicted_minutes,
            assigned_tech_id,
            status,
            vehicle:vehicles(year, make, model)
          ),
          bay:shop_bays(id, name, bay_type)
        `)
        .eq('shop_id', activeShop.id)
        .eq('status', 'open')
        .order('scheduled_start', { ascending: true });

      if (fetchError) throw fetchError;
      // Cast status from string to union type
      setAlerts((data || []).map(a => {
        const row = a as Record<string, unknown>;
        return {
          ...(a as object),
          status: a.status as WeatherAlert['status'],
          reason: a.reason as WeatherAlert['reason'],
          // Tolerant of rows that predate the provenance migration, and of
          // a deployment where the migration has not yet been applied.
          observation_kind:
            (row.observation_kind as WeatherAlert['observation_kind']) ?? null,
          weather_effective_at: (row.weather_effective_at as string) ?? null,
          weather_source: (row.weather_source as string) ?? null,
          policy_threshold: (row.policy_threshold as string) ?? null,
          policy_explanation: (row.policy_explanation as string) ?? null,
        } as WeatherAlert;
      }));
      setError(null);
    } catch (err: any) {
      console.error('[useWeatherAlerts] Error loading alerts:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Batch 11 (2026-05-29): weather alerts switched from Supabase
  // realtime to 10-minute polling.
  //
  // Why: weather conditions change on weather-station cadence (~10
  // min), not on operator action. The pre-bump realtime subscription
  // existed because the original Chronomancer R-RT-6 doctrine treated
  // every "safety signal" as a realtime subscriber — but the source
  // table (`weather_alerts`) is itself populated by a periodic
  // server-side weather-poll, so per-row UPDATE/INSERT realtime adds
  // no faster signal than a 10-min poll while paying the websocket
  // cost AND emitting the red CLOSED log every time the user
  // changes shops / navigates / unmounts the alerts surface.
  //
  // Polling fetches immediately on mount + every 10 minutes
  // thereafter. `clearInterval` on unmount or shop change prevents
  // any stale poll from firing into a different shop's state. Errors
  // surface via the existing `loadAlerts` catch (line 65); no new
  // log channel needed.
  useEffect(() => {
    if (!activeShop?.id) return;

    loadAlerts();
    const POLL_MS = 10 * 60 * 1000; // 10 minutes
    const intervalId = window.setInterval(() => {
      loadAlerts();
    }, POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeShop?.id]);

  const dismissAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('weather_alerts')
        .update({ 
          status: 'dismissed',
          updated_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) throw error;
      
      // Optimistic update
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err: any) {
      console.error('[useWeatherAlerts] Error dismissing alert:', err);
      throw err;
    }
  };

  const resolveAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('weather_alerts')
        .update({ 
          status: 'resolved',
          updated_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) throw error;
      
      // Optimistic update
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err: any) {
      console.error('[useWeatherAlerts] Error resolving alert:', err);
      throw err;
    }
  };

  const openAlerts = alerts.filter(a => a.status === 'open');

  return {
    alerts,
    openAlerts,
    isLoading,
    error,
    refetch: loadAlerts,
    dismissAlert,
    resolveAlert
  };
}
